import React, { useLayoutEffect, useRef } from "react";
import { HidesScrolledTooltips } from "./Tooltip";

type OverlayScrollProps = React.ComponentProps<"div"> & {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  /// The wrapper the thumb is placed in, where a flex parent's sizing goes.
  frameClassName?: string;
  /// `both` also scrolls across, under a thumb on the bottom edge.
  axis?: "y" | "both";
  /// `hover` shows the thumbs only while the pointer is over the frame, as a
  /// spreadsheet's do.
  reveal?: "always" | "hover";
};

type Axis = "y" | "x";
const DIM = {
  y: {
    scroll: "scrollTop",
    total: "scrollHeight",
    view: "clientHeight",
    min: "minHeight",
    length: "height",
    start: "top",
    pointer: "clientY",
  },
  x: {
    scroll: "scrollLeft",
    total: "scrollWidth",
    view: "clientWidth",
    min: "minWidth",
    length: "width",
    start: "left",
    pointer: "clientX",
  },
} as const;

/** Scrolls under thumbs drawn over its edges: a native bar takes width,
 *  which would pull the rows off a header that sits outside the scroller,
 *  and a Mac's overlay bar can't be seen, so can't be grabbed, until it moves. */
export const OverlayScroll: React.FC<OverlayScrollProps> = ({
  scrollRef,
  frameClassName = "",
  className = "",
  axis = "y",
  reveal = "always",
  children,
  ...rest
}) => {
  const ownRef = useRef<HTMLDivElement>(null);
  const box = scrollRef ?? ownRef;
  const content = useRef<HTMLDivElement>(null);
  const thumbs = {
    y: useRef<HTMLDivElement>(null),
    x: useRef<HTMLDivElement>(null),
  };
  // Scroll per pixel of thumb travel, as last laid out.
  const rate = useRef({ y: 1, x: 1 });
  const drag = useRef({ at: 0, from: 0 });
  const axes: Axis[] = axis === "both" ? ["y", "x"] : ["y"];

  useLayoutEffect(() => {
    const el = box.current!;
    const place = () => {
      for (const a of axes) {
        const bar = thumbs[a].current!;
        const d = DIM[a];
        const view = el[d.view];
        const total = el[d.total];
        bar.hidden = total <= view;
        if (bar.hidden) continue;
        const min = parseFloat(getComputedStyle(bar)[d.min]) || 0;
        const length = Math.max((view * view) / total, min);
        rate.current[a] = (total - view) / Math.max(1, view - length);
        bar.style[d.length] = `${length}px`;
        bar.style[d.start] = `${el[d.scroll] / rate.current[a]}px`;
      }
    };
    // Chrome on macOS hands Shift + a mouse wheel over as vertical; where the
    // platform already turned it, `deltaX` carries it and this stands aside.
    const across = (e: WheelEvent) => {
      if (!e.shiftKey || e.deltaX !== 0 || el.scrollWidth <= el.clientWidth)
        return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    place();
    el.addEventListener("scroll", place, { passive: true });
    if (axis === "both")
      el.addEventListener("wheel", across, { passive: false });
    const observer = new ResizeObserver(place);
    observer.observe(el);
    observer.observe(content.current!);
    return () => {
      el.removeEventListener("scroll", place);
      el.removeEventListener("wheel", across);
      observer.disconnect();
    };
    // `axes` follows `axis`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, axis]);

  const hover = reveal === "hover";
  return (
    <div
      className={`relative ${hover ? "group/overlay" : ""} ${frameClassName}`}
    >
      <div
        ref={box}
        {...rest}
        className={`${className} ${axis === "both" ? "overflow-auto" : "overflow-y-auto"} [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      >
        <div
          ref={content}
          className={axis === "both" ? "w-max min-w-full" : undefined}
        >
          <HidesScrolledTooltips.Provider value>
            {children}
          </HidesScrolledTooltips.Provider>
        </div>
      </div>
      {axes.map((a) => (
        <div
          key={a}
          ref={thumbs[a]}
          aria-hidden
          data-testid={a === "y" ? "overlay-thumb" : "overlay-thumb-x"}
          className={`scrollbar-thumb absolute z-20 ${
            a === "y"
              ? "right-0 w-(--size-scrollbar) min-h-(--size-scrollbar-thumb-min)"
              : "bottom-0 h-(--size-scrollbar) min-w-(--size-scrollbar-thumb-min)"
          } ${hover ? "opacity-0 transition-opacity group-hover/overlay:opacity-100 [&.is-dragging]:opacity-100" : ""}`}
          style={
            hover ? { transitionDuration: "var(--duration-micro)" } : undefined
          }
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            e.currentTarget.classList.add("is-dragging");
            drag.current = {
              at: e[DIM[a].pointer],
              from: box.current![DIM[a].scroll],
            };
          }}
          onPointerMove={(e) => {
            if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
            box.current![DIM[a].scroll] =
              drag.current.from +
              (e[DIM[a].pointer] - drag.current.at) * rate.current[a];
          }}
          onLostPointerCapture={(e) =>
            e.currentTarget.classList.remove("is-dragging")
          }
        />
      ))}
    </div>
  );
};
